import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useLms } from '../../context/LmsContext';
import { Card, FormRow, PageHeader, StatusBadge } from '../../components/ui';
import CoursePreview from '../../components/CoursePreview';
import { byId, getExercisesForVersion, getLessonsForModule, getModulesForVersion, getQuizzesForVersion } from '../../utils/lmsSelectors';

const RESOURCE_TYPES = [
  { value: 'sop_link', label: 'SOP / internal resource' },
  { value: 'doc', label: 'Google Doc' },
  { value: 'sheet', label: 'Google Sheet' },
  { value: 'pdf', label: 'PDF' },
  { value: 'template', label: 'Template' },
  { value: 'external_url', label: 'External URL' }
];

const RESOURCE_TYPE_LABELS = {
  google_drive_video: 'Google Drive video link',
  exercise_file: 'Exercise file / Google Drive folder',
  sop_link: 'SOP / internal resource',
  drive_link: 'SOP / internal resource',
  doc: 'Google Doc',
  sheet: 'Google Sheet',
  pdf: 'PDF',
  template: 'Template',
  external_url: 'External URL'
};

function BuilderTab({ active, id, children, onClick }) {
  const isActive = active === id;
  return (
    <button
      type="button"
      role="tab"
      aria-selected={isActive}
      className={`builder-tab ${isActive ? 'active' : ''}`}
      onClick={() => onClick(id)}
    >
      {children}
    </button>
  );
}

function BuilderModal({ title, subtitle, onClose, children }) {
  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
      <div className="builder-modal" role="dialog" aria-modal="true" aria-label={title} onMouseDown={(event) => event.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h2>{title}</h2>
            {subtitle && <p className="muted small">{subtitle}</p>}
          </div>
          <button className="button button-ghost" type="button" onClick={onClose}>Close</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function emptyLessonForm() {
  return {
    id: '',
    moduleId: '',
    title: '',
    lessonType: 'reading',
    completionType: 'manual_ack',
    estimatedMinutes: 20,
    contentHtml: '',
    videoUrl: '',
    resourceId: '',
    resourceTitle: '',
    resourceUrl: '',
    resourceType: 'sop_link',
    sessionDate: '',
    meetingLink: '',
    isRequired: true
  };
}


function emptyExerciseForm(moduleId = '') {
  return {
    id: '',
    moduleId,
    title: 'Graded Exercise',
    instructions: '',
    exerciseFileTitle: 'Exercise file',
    exerciseFileUrl: '',
    passingScore: 80,
    maxScore: 100,
    isRequired: true
  };
}

function emptyQuestionForm(defaultQuizId = '') {
  return {
    id: '',
    quizId: defaultQuizId,
    prompt: '',
    optionA: '',
    optionB: '',
    optionC: '',
    correct: 'A',
    points: 10,
    explanation: ''
  };
}

function firstResource(lesson) {
  return lesson?.resources?.[0] || null;
}

function normalizeResourceType(type) {
  if (!type || type === 'drive_link') return 'sop_link';
  return type;
}

function getResourceTypeLabel(type) {
  return RESOURCE_TYPE_LABELS[type] || RESOURCE_TYPE_LABELS[normalizeResourceType(type)] || 'Resource';
}

function resourcePayloadFromLessonForm(form) {
  const isVideo = form.lessonType === 'video';
  const isExercise = form.lessonType === 'exercise';
  const url = form.resourceUrl?.trim();
  if (!url) return [];

  return [{
    id: form.resourceId || `res-${Date.now()}`,
    title: form.resourceTitle?.trim() || (isVideo ? 'Google Drive training video' : isExercise ? 'Exercise file' : 'Lesson resource'),
    resourceType: isVideo ? 'google_drive_video' : isExercise ? 'exercise_file' : normalizeResourceType(form.resourceType),
    url,
    isDownloadable: isExercise,
    sortOrder: 1
  }];
}

function lessonToForm(lesson) {
  const resource = firstResource(lesson);
  const isVideo = lesson.lessonType === 'video';
  const isExercise = lesson.lessonType === 'exercise';
  const videoUrl = lesson.videoUrl || resource?.url || '';

  return {
    id: lesson.id,
    moduleId: lesson.moduleId,
    title: lesson.title || '',
    lessonType: lesson.lessonType || 'reading',
    completionType: lesson.completionType || 'manual_ack',
    estimatedMinutes: lesson.estimatedMinutes || 20,
    contentHtml: lesson.contentHtml || '',
    videoUrl,
    resourceId: resource?.id || '',
    resourceTitle: resource?.title || (isVideo ? 'Google Drive training video' : isExercise ? 'Exercise file' : ''),
    resourceUrl: isVideo ? videoUrl : resource?.url || '',
    resourceType: isVideo ? 'google_drive_video' : isExercise ? 'exercise_file' : normalizeResourceType(resource?.resourceType),
    sessionDate: lesson.sessionDate || '',
    meetingLink: lesson.meetingLink || '',
    isRequired: lesson.isRequired !== false
  };
}

function questionToForm(quiz, question) {
  const correctIndex = (question.options || []).findIndex((option) => option.isCorrect);
  return {
    id: question.id,
    quizId: quiz.id,
    prompt: question.prompt || '',
    optionA: question.options?.[0]?.optionText || '',
    optionB: question.options?.[1]?.optionText || '',
    optionC: question.options?.[2]?.optionText || '',
    correct: ['A', 'B', 'C'][Math.max(0, correctIndex)] || 'A',
    points: question.points || 10,
    explanation: question.explanation || ''
  };
}


function exerciseToForm(exercise) {
  return {
    id: exercise.id,
    moduleId: exercise.moduleId || '',
    title: exercise.title || '',
    instructions: exercise.instructions || '',
    exerciseFileTitle: exercise.exerciseFileTitle || 'Exercise file',
    exerciseFileUrl: exercise.exerciseFileUrl || '',
    passingScore: exercise.passingScore || 80,
    maxScore: exercise.maxScore || 100,
    isRequired: exercise.isRequired !== false
  };
}

function correctAnswerText(question) {
  return question.options?.find((option) => option.isCorrect)?.optionText || 'Not set';
}

function quizPlacementLabel(data, quiz) {
  const lesson = quiz.afterLessonId ? byId(data.lessons, quiz.afterLessonId) : null;
  if (quiz.placement === 'after_lesson') return `After lesson: ${lesson?.title || 'Selected lesson'}`;
  if (quiz.placement === 'both') return `After lesson: ${lesson?.title || 'Selected lesson'} + Module end`;
  return 'Module end';
}

export default function CourseBuilder() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const {
    data,
    currentUser,
    updateCourse,
    addModule,
    updateModule,
    addLesson,
    updateLesson,
    addQuiz,
    addQuizQuestion,
    addExercise,
    updateExercise,
    updateQuizQuestion,
    deleteCourse,
    deleteModule,
    deleteLesson,
    deleteQuiz,
    deleteExercise,
    deleteQuizQuestion,
    submitCourseForReview,
    publishCourse
  } = useLms();
  const course = data.courses.find((item) => item.id === courseId);
  const [tab, setTab] = useState('info');
  const [courseForm, setCourseForm] = useState(course || {});
  const [moduleTitle, setModuleTitle] = useState('');
  const [lessonForm, setLessonForm] = useState(emptyLessonForm());
  const [quizForm, setQuizForm] = useState({ moduleId: '', lessonId: '', placement: 'module_end', title: 'Knowledge Check', passingScore: data.settings.defaultPassingScore, maxAttempts: data.settings.defaultMaxAttempts });
  const [exerciseForm, setExerciseForm] = useState(emptyExerciseForm());
  const [questionForm, setQuestionForm] = useState(emptyQuestionForm());
  const [builderModal, setBuilderModal] = useState(null);
  const [message, setMessage] = useState('');

  const version = byId(data.courseVersions, course?.currentVersionId);
  const modules = useMemo(() => getModulesForVersion(data, course?.currentVersionId), [data, course]);
  const quizzes = useMemo(() => getQuizzesForVersion(data, course?.currentVersionId), [data, course]);
  const exercises = useMemo(() => getExercisesForVersion(data, course?.currentVersionId), [data, course]);

  if (!course) return <Card>Course not found.</Card>;

  const closeModal = () => setBuilderModal(null);

  const saveCourse = (event) => {
    event.preventDefault();
    const result = updateCourse(course.id, courseForm, currentUser.id);
    setMessage(result.ok ? 'Course info saved.' : result.message);
  };

  const submitModule = (event) => {
    event.preventDefault();
    if (!moduleTitle.trim()) return;
    addModule(course.currentVersionId, { title: moduleTitle }, currentUser.id);
    setModuleTitle('');
    setMessage('Module added. You can now add lessons or quizzes inside this module.');
    closeModal();
  };

  const submitLesson = (event) => {
    event.preventDefault();
    const moduleId = lessonForm.moduleId || modules[0]?.id;
    if (!moduleId) return;

    const payload = {
      moduleId,
      title: lessonForm.title,
      lessonType: lessonForm.lessonType,
      completionType: lessonForm.completionType,
      estimatedMinutes: lessonForm.estimatedMinutes,
      contentHtml: lessonForm.contentHtml,
      videoUrl: lessonForm.lessonType === 'video' ? lessonForm.resourceUrl.trim() : '',
      resources: resourcePayloadFromLessonForm(lessonForm),
      sessionDate: lessonForm.lessonType === 'practical' ? lessonForm.sessionDate : '',
      meetingLink: lessonForm.lessonType === 'practical' ? lessonForm.meetingLink.trim() : '',
      isRequired: lessonForm.isRequired
    };

    if (lessonForm.id) {
      updateLesson(lessonForm.id, payload, currentUser.id);
      setMessage('Lesson updated. Learners will see the latest content/resource in the course lesson.');
    } else {
      addLesson(moduleId, payload, currentUser.id);
      setMessage('Lesson added. Use Add quiz beside the lesson if this lesson needs its own knowledge check.');
    }
    setLessonForm(emptyLessonForm());
    closeModal();
  };

  const submitQuiz = (event) => {
    event.preventDefault();
    const moduleId = quizForm.moduleId || modules[0]?.id;
    if (!moduleId) return;
    const lessonOptions = getLessonsForModule(data, moduleId);
    const afterLessonId = quizForm.placement === 'module_end' ? '' : quizForm.lessonId || lessonOptions[0]?.id || '';
    if ((quizForm.placement === 'after_lesson' || quizForm.placement === 'both') && !afterLessonId) {
      setMessage('Select a lesson first before adding an after-lesson quiz.');
      return;
    }
    addQuiz(course.currentVersionId, moduleId, { ...quizForm, moduleId, afterLessonId }, currentUser.id);
    setQuizForm({ moduleId: '', lessonId: '', placement: 'module_end', title: 'Knowledge Check', passingScore: data.settings.defaultPassingScore, maxAttempts: data.settings.defaultMaxAttempts });
    setMessage('Quiz added with the selected placement. Add questions to the quiz next.');
    closeModal();
  };


  const submitExercise = (event) => {
    event.preventDefault();
    const moduleId = exerciseForm.moduleId || modules[0]?.id;
    if (!moduleId) return;
    const payload = {
      moduleId,
      title: exerciseForm.title,
      instructions: exerciseForm.instructions,
      exerciseFileTitle: exerciseForm.exerciseFileTitle,
      exerciseFileUrl: exerciseForm.exerciseFileUrl,
      passingScore: exerciseForm.passingScore,
      maxScore: exerciseForm.maxScore,
      isRequired: exerciseForm.isRequired
    };
    if (exerciseForm.id) {
      updateExercise(exerciseForm.id, payload, currentUser.id);
      setMessage('Exercise updated. Learners will see the latest instructions and file link.');
    } else {
      addExercise(course.currentVersionId, moduleId, payload, currentUser.id);
      setMessage('Exercise added. Learners will submit a Google Drive link and trainers will grade it.');
    }
    setExerciseForm(emptyExerciseForm());
    closeModal();
  };

  const submitQuestion = (event) => {
    event.preventDefault();
    const quizId = questionForm.quizId || quizzes[0]?.id;
    if (!quizId) return;

    if (questionForm.id) {
      updateQuizQuestion(quizId, questionForm.id, questionForm, currentUser.id);
      setMessage('Question updated.');
    } else {
      addQuizQuestion(quizId, questionForm, currentUser.id);
      setMessage('Question added.');
    }
    setQuestionForm(emptyQuestionForm(quizId));
    closeModal();
  };

  const handleLessonTypeChange = (lessonType) => {
    const completionType = lessonType === 'video' ? 'video_ack' : lessonType === 'practical' ? 'trainer_approval' : 'manual_ack';
    const wasSpecialResource = lessonForm.resourceType === 'google_drive_video' || lessonForm.resourceType === 'exercise_file';

    setLessonForm({
      ...lessonForm,
      lessonType,
      completionType,
      videoUrl: lessonType === 'video' ? lessonForm.videoUrl : '',
      resourceType: lessonType === 'video' ? 'google_drive_video' : wasSpecialResource ? 'sop_link' : normalizeResourceType(lessonForm.resourceType),
      resourceTitle: lessonType === 'video' ? lessonForm.resourceTitle || 'Google Drive training video' : wasSpecialResource ? '' : lessonForm.resourceTitle,
      resourceUrl: lessonType === 'video' ? lessonForm.resourceUrl || lessonForm.videoUrl : wasSpecialResource ? '' : lessonForm.resourceUrl,
      sessionDate: lessonType === 'practical' ? lessonForm.sessionDate : '',
      meetingLink: lessonType === 'practical' ? lessonForm.meetingLink : ''
    });
  };

  const selectLessonForEdit = (lesson) => {
    setLessonForm(lessonToForm(lesson));
    setBuilderModal('lesson');
    setMessage('Editing lesson. Update the content/resource, then save changes.');
  };

  const selectQuestionForEdit = (quiz, question) => {
    setQuestionForm(questionToForm(quiz, question));
    setBuilderModal('question');
    setMessage('Editing existing question. Update the prompt, options, correct answer, or explanation, then save changes.');
  };


  const selectExerciseForEdit = (exercise) => {
    setExerciseForm(exerciseToForm(exercise));
    setBuilderModal('exercise');
    setMessage('Editing exercise. Update instructions, file link, or grading settings, then save.');
  };

  const prepareAddModule = () => {
    setTab('modules');
    setModuleTitle('');
    setBuilderModal('module');
    setMessage('Adding module. Enter the module title in the modal.');
  };

  const prepareAddLesson = (moduleId) => {
    const targetModule = byId(data.modules, moduleId);
    setTab('modules');
    setLessonForm({ ...emptyLessonForm(), moduleId });
    setBuilderModal('lesson');
    setMessage(`Adding lesson inside ${targetModule?.title || 'the selected module'}.`);
  };

  const prepareAddQuiz = (moduleId, placement = 'module_end', lessonId = '') => {
    const targetModule = byId(data.modules, moduleId);
    const targetLesson = lessonId ? byId(data.lessons, lessonId) : null;
    setTab('quizzes');
    setQuizForm({
      moduleId,
      lessonId,
      placement,
      title: targetLesson ? `${targetLesson.title} Knowledge Check` : `${targetModule?.title || 'Module'} Knowledge Check`,
      passingScore: data.settings.defaultPassingScore,
      maxAttempts: data.settings.defaultMaxAttempts
    });
    setBuilderModal('quiz');
    setMessage(targetLesson
      ? `Adding quiz after lesson: ${targetLesson.title}. You can keep it after the lesson or change placement to both.`
      : `Adding quiz at the end of module: ${targetModule?.title || 'selected module'}.`);
  };


  const prepareAddExercise = (moduleId = modules[0]?.id || '') => {
    const targetModule = byId(data.modules, moduleId);
    setTab('exercises');
    setExerciseForm({ ...emptyExerciseForm(moduleId), passingScore: data.settings.defaultPassingScore });
    setBuilderModal('exercise');
    setMessage(`Adding graded exercise inside ${targetModule?.title || 'the selected module'}.`);
  };

  const prepareAddQuestion = (quizId = quizzes[0]?.id || '') => {
    const targetQuiz = byId(quizzes, quizId);
    setTab('quizzes');
    setQuestionForm(emptyQuestionForm(quizId));
    setBuilderModal('question');
    setMessage(targetQuiz ? `Adding question to ${targetQuiz.title}.` : 'Adding question. Select a quiz first.');
  };

  const handleDeleteCourse = () => {
    if (!window.confirm(`Delete course "${course.title}"? This cannot be undone.`)) return;
    const result = deleteCourse(course.id, currentUser.id);
    if (!result.ok) {
      setMessage(result.message);
      return;
    }
    navigate('/trainer/courses', { replace: true });
  };

  const handleDeleteModule = (module) => {
    if (!window.confirm(`Delete module "${module.title}" and all lessons/quizzes inside it?`)) return;
    const result = deleteModule(module.id, currentUser.id);
    setMessage(result.ok ? 'Module deleted.' : result.message);
  };

  const handleDeleteLesson = (lesson) => {
    if (!window.confirm(`Delete lesson "${lesson.title}"? Any quiz placed after this lesson will also be removed.`)) return;
    const result = deleteLesson(lesson.id, currentUser.id);
    setMessage(result.ok ? 'Lesson deleted.' : result.message);
  };

  const handleDeleteQuiz = (quiz) => {
    if (!window.confirm(`Delete quiz "${quiz.title}" and all questions inside it?`)) return;
    const result = deleteQuiz(quiz.id, currentUser.id);
    setMessage(result.ok ? 'Quiz deleted.' : result.message);
  };


  const handleDeleteExercise = (exercise) => {
    if (!window.confirm(`Delete exercise "${exercise.title}"?`)) return;
    const result = deleteExercise(exercise.id, currentUser.id);
    setMessage(result.ok ? 'Exercise deleted.' : result.message);
  };

  const handleDeleteQuestion = (quiz, question) => {
    if (!window.confirm('Delete this question?')) return;
    const result = deleteQuizQuestion(quiz.id, question.id, currentUser.id);
    setMessage(result.ok ? 'Question deleted.' : result.message);
  };

  const saveModuleName = (module, value) => {
    const title = value.trim();
    if (!title || title === module.title) return;
    const result = updateModule(module.id, { title }, currentUser.id);
    setMessage(result.ok ? 'Module name updated.' : result.message);
  };

  const renderModuleModal = () => (
    <BuilderModal title="Add module" subtitle="Create a new section for lessons and module-end quizzes." onClose={closeModal}>
      <form className="form-stack" onSubmit={submitModule}>
        <FormRow label="Module title"><input required autoFocus value={moduleTitle} onChange={(e) => setModuleTitle(e.target.value)} placeholder="Example: PE Stamp Review Basics" /></FormRow>
        <div className="button-row modal-actions-row">
          <button className="button button-primary" type="submit">Add module</button>
          <button className="button button-ghost" type="button" onClick={closeModal}>Cancel</button>
        </div>
      </form>
    </BuilderModal>
  );

  const renderLessonModal = () => {
    const isVideo = lessonForm.lessonType === 'video';
    const isPractical = lessonForm.lessonType === 'practical';
    const isExercise = false;
    return (
      <BuilderModal
        title={lessonForm.id ? 'Edit lesson' : 'Add lesson'}
        subtitle={isVideo ? 'Video lessons use a Google Drive video link as the lesson resource.' : isPractical ? 'Practical activities can include an optional session date and meeting link. Completion requires trainer attendance confirmation.' : 'Add lesson content and a lesson resource when needed.'}
        onClose={closeModal}
      >
        <form className="form-stack" onSubmit={submitLesson}>
          <FormRow label="Module"><select value={lessonForm.moduleId} onChange={(e) => setLessonForm({ ...lessonForm, moduleId: e.target.value })}>{modules.map((module) => <option key={module.id} value={module.id}>{module.title}</option>)}</select></FormRow>
          <FormRow label="Lesson title"><input required autoFocus value={lessonForm.title} onChange={(e) => setLessonForm({ ...lessonForm, title: e.target.value })} /></FormRow>
          <FormRow label="Type"><select value={lessonForm.lessonType} onChange={(e) => handleLessonTypeChange(e.target.value)}><option value="video">Video</option><option value="reading">Reading</option><option value="resource">Resource/SOP</option><option value="practical">Practical Activity</option><option value="mixed">Mixed</option></select></FormRow>
          <div className="form-grid">
            <FormRow label="Estimated minutes"><input type="number" min="1" value={lessonForm.estimatedMinutes} onChange={(e) => setLessonForm({ ...lessonForm, estimatedMinutes: e.target.value })} /></FormRow>
            <label className="check-row align-bottom"><input type="checkbox" checked={lessonForm.isRequired} onChange={(e) => setLessonForm({ ...lessonForm, isRequired: e.target.checked })} /> Required for course completion</label>
          </div>
          <FormRow label={isPractical ? 'Practical activity instructions' : 'Lesson content / instructions'}><textarea value={lessonForm.contentHtml} onChange={(e) => setLessonForm({ ...lessonForm, contentHtml: e.target.value })} placeholder={isPractical ? 'Enter attendance instructions, agenda, or activity requirements.' : 'Enter lesson content or instructions.'} /></FormRow>

          {isPractical && (
            <div className="resource-editor">
              <h3>Practical Activity Session</h3>
              <p className="muted small">Session date and meeting link are optional when creating the lesson and can be added later.</p>
              <div className="form-grid">
                <FormRow label="Session date"><input type="datetime-local" value={lessonForm.sessionDate} onChange={(e) => setLessonForm({ ...lessonForm, sessionDate: e.target.value })} /></FormRow>
                <FormRow label="Meeting link"><input type="url" placeholder="https://meet.google.com/..." value={lessonForm.meetingLink} onChange={(e) => setLessonForm({ ...lessonForm, meetingLink: e.target.value })} /></FormRow>
              </div>
            </div>
          )}

          {!isPractical && (
          <div className="resource-editor">
            <h3>{isExercise ? 'Exercise File' : 'Lesson Resource'}</h3>
            <p className="muted small">
              {isVideo
                ? 'Resource type is fixed to Google Drive video link for Video lessons.'
                : isExercise
                  ? 'Paste the Google Drive folder or file link learners will open/download before submitting their completed exercise link.'
                  : 'Attach a supporting SOP, document, worksheet, PDF, template, or external reference. Google Drive video links are only for Video lessons.'}
            </p>
            <div className="form-grid">
              <FormRow label={isVideo ? 'Video title' : isExercise ? 'Exercise file title' : 'Resource title'}><input placeholder={isVideo ? 'Google Drive training video' : isExercise ? 'Exercise file or folder' : 'SOP, template, checklist, worksheet'} value={lessonForm.resourceTitle} onChange={(e) => setLessonForm({ ...lessonForm, resourceTitle: e.target.value })} /></FormRow>
              {isVideo ? (
                <FormRow label="Resource type"><input value="Google Drive video link" disabled readOnly /></FormRow>
              ) : isExercise ? (
                <FormRow label="Resource type"><input value="Exercise file / Google Drive folder" disabled readOnly /></FormRow>
              ) : (
                <FormRow label="Resource type"><select value={normalizeResourceType(lessonForm.resourceType)} onChange={(e) => setLessonForm({ ...lessonForm, resourceType: e.target.value })}>{RESOURCE_TYPES.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}</select></FormRow>
              )}
            </div>
            <FormRow label={isVideo ? 'Google Drive video URL' : isExercise ? 'Exercise Google Drive folder/file URL' : 'Resource URL'}>
              <input required={isVideo || isExercise} placeholder={isVideo ? 'Paste Google Drive video share link or /preview embed URL' : isExercise ? 'Paste the Google Drive folder or exercise file link' : 'Paste SOP/resource link'} value={lessonForm.resourceUrl} onChange={(e) => setLessonForm({ ...lessonForm, resourceUrl: e.target.value })} />
              {isVideo && <small className="muted">Recommended format: https://drive.google.com/file/d/FILE_ID/preview.</small>}
              {isExercise && <small className="muted">Learners will open this link, complete the exercise, then submit their completed work as a Google Drive link.</small>}
            </FormRow>
          </div>
          )}

          <div className="button-row modal-actions-row">
            <button className="button button-primary" type="submit" disabled={!modules.length}>{lessonForm.id ? 'Save lesson changes' : 'Add lesson'}</button>
            <button className="button button-ghost" type="button" onClick={closeModal}>Cancel</button>
          </div>
        </form>
      </BuilderModal>
    );
  };

  const renderQuizModal = () => (
    <BuilderModal title="Add quiz" subtitle="Place the quiz after a lesson, at the end of a module, or both." onClose={closeModal}>
      <form className="form-stack" onSubmit={submitQuiz}>
        <FormRow label="Quiz title"><input required autoFocus value={quizForm.title} onChange={(e) => setQuizForm({ ...quizForm, title: e.target.value })} /></FormRow>
        <div className="form-grid">
          <FormRow label="Module">
            <select value={quizForm.moduleId} onChange={(e) => setQuizForm({ ...quizForm, moduleId: e.target.value, lessonId: '' })}>{modules.map((module) => <option key={module.id} value={module.id}>{module.title}</option>)}</select>
          </FormRow>
          <FormRow label="Quiz placement">
            <select value={quizForm.placement} onChange={(e) => setQuizForm({ ...quizForm, placement: e.target.value })}>
              <option value="after_lesson">After selected lesson</option>
              <option value="module_end">End of selected module</option>
              <option value="both">After selected lesson and module end</option>
            </select>
          </FormRow>
        </div>
        {(quizForm.placement === 'after_lesson' || quizForm.placement === 'both') && (
          <FormRow label="Place after lesson">
            <select value={quizForm.lessonId} onChange={(e) => setQuizForm({ ...quizForm, lessonId: e.target.value })}>
              {getLessonsForModule(data, quizForm.moduleId || modules[0]?.id).map((lesson) => <option key={lesson.id} value={lesson.id}>{lesson.title}</option>)}
            </select>
            <small className="muted">Use this when you want a knowledge check immediately after a lesson.</small>
          </FormRow>
        )}
        <div className="form-grid">
          <FormRow label="Passing score"><input type="number" min="0" max="100" value={quizForm.passingScore} onChange={(e) => setQuizForm({ ...quizForm, passingScore: e.target.value })} /></FormRow>
          <FormRow label="Max attempts"><input type="number" min="1" value={quizForm.maxAttempts} onChange={(e) => setQuizForm({ ...quizForm, maxAttempts: e.target.value })} /></FormRow>
        </div>
        <div className="button-row modal-actions-row">
          <button className="button button-primary" type="submit" disabled={!modules.length}>Add quiz</button>
          <button className="button button-ghost" type="button" onClick={closeModal}>Cancel</button>
        </div>
      </form>
    </BuilderModal>
  );


  const renderExerciseModal = () => (
    <BuilderModal title={exerciseForm.id ? 'Edit exercise' : 'Add exercise'} subtitle="Exercises are separate graded activities. Learners submit a completed Google Drive link and trainers check and grade the work." onClose={closeModal}>
      <form className="form-stack" onSubmit={submitExercise}>
        <FormRow label="Module"><select value={exerciseForm.moduleId} onChange={(e) => setExerciseForm({ ...exerciseForm, moduleId: e.target.value })}>{modules.map((module) => <option key={module.id} value={module.id}>{module.title}</option>)}</select></FormRow>
        <FormRow label="Exercise title"><input required autoFocus value={exerciseForm.title} onChange={(e) => setExerciseForm({ ...exerciseForm, title: e.target.value })} /></FormRow>
        <FormRow label="Instructions"><textarea required value={exerciseForm.instructions} onChange={(e) => setExerciseForm({ ...exerciseForm, instructions: e.target.value })} placeholder="Enter the step-by-step exercise instructions learners must complete." /></FormRow>
        <div className="form-grid">
          <FormRow label="Exercise file title"><input value={exerciseForm.exerciseFileTitle} onChange={(e) => setExerciseForm({ ...exerciseForm, exerciseFileTitle: e.target.value })} placeholder="Worksheet, CAD exercise, PVS checklist, etc." /></FormRow>
          <FormRow label="Exercise Google Drive file/folder URL"><input required type="url" value={exerciseForm.exerciseFileUrl} onChange={(e) => setExerciseForm({ ...exerciseForm, exerciseFileUrl: e.target.value })} placeholder="https://drive.google.com/..." /></FormRow>
        </div>
        <div className="form-grid">
          <FormRow label="Passing score %"><input type="number" min="0" max="100" value={exerciseForm.passingScore} onChange={(e) => setExerciseForm({ ...exerciseForm, passingScore: e.target.value })} /></FormRow>
          <FormRow label="Max score"><input type="number" min="1" value={exerciseForm.maxScore} onChange={(e) => setExerciseForm({ ...exerciseForm, maxScore: e.target.value })} /></FormRow>
        </div>
        <label className="check-row"><input type="checkbox" checked={exerciseForm.isRequired} onChange={(e) => setExerciseForm({ ...exerciseForm, isRequired: e.target.checked })} /> Required for course completion</label>
        <div className="button-row modal-actions-row">
          <button className="button button-primary" type="submit" disabled={!modules.length}>{exerciseForm.id ? 'Save exercise changes' : 'Add exercise'}</button>
          <button className="button button-ghost" type="button" onClick={closeModal}>Cancel</button>
        </div>
      </form>
    </BuilderModal>
  );

  const renderQuestionModal = () => (
    <BuilderModal title={questionForm.id ? 'Edit question' : 'Add question'} subtitle="Update the prompt, answer choices, correct answer, points, and explanation." onClose={closeModal}>
      <form className="form-stack" onSubmit={submitQuestion}>
        <FormRow label="Quiz"><select value={questionForm.quizId} onChange={(e) => setQuestionForm({ ...questionForm, quizId: e.target.value })}>{quizzes.map((quiz) => <option key={quiz.id} value={quiz.id}>{quiz.title}</option>)}</select></FormRow>
        <FormRow label="Prompt"><textarea required autoFocus value={questionForm.prompt} onChange={(e) => setQuestionForm({ ...questionForm, prompt: e.target.value })} /></FormRow>
        <FormRow label="Option A"><input required value={questionForm.optionA} onChange={(e) => setQuestionForm({ ...questionForm, optionA: e.target.value })} /></FormRow>
        <FormRow label="Option B"><input required value={questionForm.optionB} onChange={(e) => setQuestionForm({ ...questionForm, optionB: e.target.value })} /></FormRow>
        <FormRow label="Option C"><input required value={questionForm.optionC} onChange={(e) => setQuestionForm({ ...questionForm, optionC: e.target.value })} /></FormRow>
        <div className="form-grid">
          <FormRow label="Correct answer"><select value={questionForm.correct} onChange={(e) => setQuestionForm({ ...questionForm, correct: e.target.value })}><option>A</option><option>B</option><option>C</option></select></FormRow>
          <FormRow label="Points"><input type="number" min="1" value={questionForm.points} onChange={(e) => setQuestionForm({ ...questionForm, points: e.target.value })} /></FormRow>
        </div>
        <FormRow label="Explanation"><textarea value={questionForm.explanation} onChange={(e) => setQuestionForm({ ...questionForm, explanation: e.target.value })} /></FormRow>
        <div className="button-row modal-actions-row">
          <button className="button button-primary" type="submit" disabled={!quizzes.length}>{questionForm.id ? 'Save question changes' : 'Add question'}</button>
          <button className="button button-ghost" type="button" onClick={closeModal}>Cancel</button>
        </div>
      </form>
    </BuilderModal>
  );

  return (
    <>
      <PageHeader
        title={course.title}
        description="Build modules, lessons, Google Drive video lessons, resources, quizzes, graded exercises, practical activities, and completion rules before publishing."
        action={(
          <div className="button-row compact">
            <Link className="button button-secondary" to={currentUser.role === 'admin' ? `/admin/courses/${course.id}` : '/trainer/courses'}>Back</Link>
            <button className="button button-danger" type="button" onClick={handleDeleteCourse}>Delete course</button>
          </div>
        )}
      />
      <Card>
        <div className="builder-topline">
          <StatusBadge status={course.status} />
          <span>Version {version?.versionNumber}</span>
          <span>{modules.length} modules</span>
          <span>{quizzes.length} quizzes</span>
          <span>{exercises.length} exercises</span>
        </div>
        <div className="builder-tabs" role="tablist" aria-label="Course builder sections">
          <BuilderTab active={tab} id="info" onClick={setTab}>Course Info</BuilderTab>
          <BuilderTab active={tab} id="modules" onClick={setTab}>Modules & Lessons</BuilderTab>
          <BuilderTab active={tab} id="quizzes" onClick={setTab}>Quizzes</BuilderTab>
          <BuilderTab active={tab} id="exercises" onClick={setTab}>Exercises</BuilderTab>
          <BuilderTab active={tab} id="rules" onClick={setTab}>Completion Rules</BuilderTab>
          <BuilderTab active={tab} id="preview" onClick={setTab}>Preview</BuilderTab>
        </div>
        {message && <p className="feedback-box compact-feedback">{message}</p>}
      </Card>

      {tab === 'info' && (
        <Card>
          <h2>Course information</h2>
          <form className="form-stack" onSubmit={saveCourse}>
            <FormRow label="Title"><input value={courseForm.title || ''} onChange={(e) => setCourseForm({ ...courseForm, title: e.target.value })} /></FormRow>
            <FormRow label="Description"><textarea value={courseForm.description || ''} onChange={(e) => setCourseForm({ ...courseForm, description: e.target.value })} /></FormRow>
            <div className="form-grid">
              <FormRow label="Category"><select value={courseForm.category || ''} onChange={(e) => setCourseForm({ ...courseForm, category: e.target.value })}>{data.categories.map((category) => <option key={category}>{category}</option>)}</select></FormRow>
              <FormRow label="Difficulty"><select value={courseForm.difficulty || 'beginner'} onChange={(e) => setCourseForm({ ...courseForm, difficulty: e.target.value })}><option>beginner</option><option>intermediate</option><option>advanced</option></select></FormRow>
            </div>
            <FormRow label="Target audience"><input value={courseForm.targetAudience || ''} onChange={(e) => setCourseForm({ ...courseForm, targetAudience: e.target.value })} /></FormRow>
            <label className="check-row"><input type="checkbox" checked={Boolean(courseForm.certificateEnabled)} onChange={(e) => setCourseForm({ ...courseForm, certificateEnabled: e.target.checked })} /> Enable certificate after completion</label>
            <button className="button button-primary" type="submit">Save course info</button>
          </form>
        </Card>
      )}

      {tab === 'modules' && (
        <Card>
          <div className="section-heading">
            <div>
              <h2>Current modules and lessons</h2>
              <p className="muted small">Use modal actions to add modules, add lessons, or place quizzes after lessons/module end.</p>
            </div>
            <button className="button button-secondary" type="button" onClick={prepareAddModule}>Add module</button>
          </div>
          {modules.map((module) => {
            const moduleLessons = getLessonsForModule(data, module.id);
            return (
              <div key={module.id} className="module-block">
                <div className="module-header">
                  <div>
                    <label className="module-title-editor">
                      <span>Module name</span>
                      <input defaultValue={module.title} onBlur={(event) => saveModuleName(module, event.target.value)} />
                    </label>
                    <p className="muted small">{module.description}</p>
                  </div>
                  <div className="button-row compact module-actions">
                    <button className="button button-secondary" type="button" onClick={() => prepareAddLesson(module.id)}>Add lesson</button>
                    <button className="button button-secondary" type="button" onClick={() => prepareAddExercise(module.id)}>Add exercise</button>
                    <button className="button button-ghost" type="button" onClick={() => prepareAddQuiz(module.id, 'module_end')}>Add quiz at module end</button>
                    <button className="button button-danger" type="button" onClick={() => handleDeleteModule(module)}>Delete module</button>
                  </div>
                </div>
                {moduleLessons.length === 0 ? (
                  <p className="empty-inline">No lessons yet. Use Add lesson to start this module.</p>
                ) : (
                  <ol className="lesson-admin-list">
                    {moduleLessons.map((lesson) => (
                      <li key={lesson.id}>
                        <div>
                          <strong>{lesson.title}</strong>
                          <p className="muted small">{lesson.lessonType} • {lesson.estimatedMinutes} min</p>
                          {lesson.lessonType === 'video' && lesson.videoUrl && <p className="small link-line">Google Drive video: <a href={lesson.videoUrl} target="_blank" rel="noreferrer">{lesson.videoUrl}</a></p>}
                          {lesson.resources?.length > 0 && lesson.lessonType !== 'video' && <p className="small link-line">{getResourceTypeLabel(lesson.resources[0].resourceType)}: <a href={lesson.resources[0].url} target="_blank" rel="noreferrer">{lesson.resources[0].title}</a></p>}
                          {lesson.lessonType === 'practical' && (lesson.sessionDate || lesson.meetingLink) && <p className="small link-line">Session: {lesson.sessionDate ? new Date(lesson.sessionDate).toLocaleString() : 'Date not set'}{lesson.meetingLink && <> • <a href={lesson.meetingLink} target="_blank" rel="noreferrer">Meeting link</a></>}</p>}
                        </div>
                        <div className="lesson-actions">
                          <button className="button button-ghost" type="button" onClick={() => selectLessonForEdit(lesson)}>Edit lesson</button>
                          <button className="button button-secondary" type="button" onClick={() => prepareAddQuiz(module.id, 'after_lesson', lesson.id)}>Add quiz</button>
                          <button className="button button-danger" type="button" onClick={() => handleDeleteLesson(lesson)}>Delete lesson</button>
                        </div>
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            );
          })}
        </Card>
      )}

      {tab === 'quizzes' && (
        <Card>
          <div className="section-heading">
            <div>
              <h2>Quizzes</h2>
              <p className="muted small">Add quizzes through a modal, then add or edit questions for each quiz.</p>
            </div>
            <button className="button button-secondary" type="button" onClick={() => prepareAddQuiz(modules[0]?.id, 'module_end')}>Add quiz</button>
          </div>
          {quizzes.length === 0 ? (
            <p className="empty-inline">No quizzes yet. Use Add quiz to create a lesson quiz or module-end quiz.</p>
          ) : quizzes.map((quiz) => (
            <div key={quiz.id} className="module-block">
              <div className="module-header">
                <div>
                  <h3>{quiz.title}</h3>
                  <p className="muted small">{quizPlacementLabel(data, quiz)} • Passing score {quiz.passingScore}% • {quiz.maxAttempts} attempts • {quiz.questions.length} questions</p>
                </div>
                <div className="button-row compact">
                  <button className="button button-secondary" type="button" onClick={() => prepareAddQuestion(quiz.id)}>Add question</button>
                  <button className="button button-danger" type="button" onClick={() => handleDeleteQuiz(quiz)}>Delete quiz</button>
                </div>
              </div>
              {quiz.questions.length === 0 ? (
                <p className="empty-inline">No questions yet. Add at least one question before publishing.</p>
              ) : (
                <ol className="question-admin-list">
                  {quiz.questions.map((question) => (
                    <li key={question.id}>
                      <div>
                        <strong>{question.prompt}</strong>
                        <p className="muted small">Correct answer: {correctAnswerText(question)} • {question.points} points</p>
                      </div>
                      <div className="button-row compact">
                        <button className="button button-ghost" type="button" onClick={() => selectQuestionForEdit(quiz, question)}>Edit question</button>
                        <button className="button button-danger" type="button" onClick={() => handleDeleteQuestion(quiz, question)}>Delete question</button>
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          ))}
        </Card>
      )}


      {tab === 'exercises' && (
        <Card>
          <div className="section-heading">
            <div>
              <h2>Exercises</h2>
              <p className="muted small">Exercises are graded manually by a trainer after the learner submits a completed Google Drive link.</p>
            </div>
            <button className="button button-secondary" type="button" onClick={() => prepareAddExercise(modules[0]?.id)}>Add exercise</button>
          </div>
          {exercises.length === 0 ? (
            <p className="empty-inline">No exercises yet. Use Add exercise to create a graded activity.</p>
          ) : exercises.map((exercise) => {
            const module = byId(data.modules, exercise.moduleId);
            return (
              <div key={exercise.id} className="module-block">
                <div className="module-header">
                  <div>
                    <h3>{exercise.title}</h3>
                    <p className="muted small">{module?.title || 'Module'} • Passing score {exercise.passingScore}% • Max score {exercise.maxScore}</p>
                    {exercise.exerciseFileUrl && <p className="small link-line">Exercise file: <a href={exercise.exerciseFileUrl} target="_blank" rel="noreferrer">{exercise.exerciseFileTitle || exercise.exerciseFileUrl}</a></p>}
                    <p className="muted small">{exercise.instructions}</p>
                  </div>
                  <div className="button-row compact">
                    <button className="button button-ghost" type="button" onClick={() => selectExerciseForEdit(exercise)}>Edit exercise</button>
                    <button className="button button-danger" type="button" onClick={() => handleDeleteExercise(exercise)}>Delete exercise</button>
                  </div>
                </div>
              </div>
            );
          })}
        </Card>
      )}

      {tab === 'rules' && (
        <Card>
          <h2>Completion rules</h2>
          <ul className="check-list">
            <li>All required lessons must be completed by the learner. Practical activities require trainer/admin approval.</li>
            <li>Video lessons use Lesson Resource with resource type fixed to Google Drive video link.</li>
            <li>All required quizzes must be passed using the configured passing score.</li>
            <li>All required exercises must be submitted by the learner and graded as passing by a trainer.</li>
            <li>Practical activities remain Needs Review until a trainer/admin approves them. Exercises remain Needs Review until a trainer grades them.</li>
            <li>Certificate is generated only when completion rules are fully satisfied.</li>
          </ul>
          <div className="button-row">
            <button className="button button-secondary" onClick={() => submitCourseForReview(course.id, currentUser.id)}>Submit for Admin review</button>
            {currentUser.role === 'admin' && <button className="button button-primary" onClick={() => publishCourse(course.id, currentUser.id)}>Publish now</button>}
          </div>
        </Card>
      )}

      {tab === 'preview' && (
        <Card>
          <div className="section-heading">
            <div>
              <h2>Learner preview</h2>
              <p className="muted small">This trainer preview uses the same learner-style course layout without recording progress.</p>
            </div>
          </div>
          <CoursePreview data={data} course={course} mode="preview" />
        </Card>
      )}

      {builderModal === 'module' && renderModuleModal()}
      {builderModal === 'lesson' && renderLessonModal()}
      {builderModal === 'quiz' && renderQuizModal()}
      {builderModal === 'exercise' && renderExerciseModal()}
      {builderModal === 'question' && renderQuestionModal()}
    </>
  );
}
